import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Clock,
  CalendarCheck,
  Save,
  Ban,
  Trash2,
  Plus,
  ToggleLeft,
  ToggleRight,
  Copy,
  ExternalLink,
  MessageCircle,
  CheckCircle2,
  Link as LinkIcon,
  Edit3,
} from "lucide-react";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../hooks/useAuth";
import {
  addBlockedSlot,
  getBlockedSlots,
  deleteBlockedSlot,
} from "../../services/firestoreService";

// ... (DAYS_OF_WEEK e DEFAULT_SCHEDULE mantidos iguais)
const DAYS_OF_WEEK = [
  { id: "monday", label: "Segunda-feira" },
  { id: "tuesday", label: "Terça-feira" },
  { id: "wednesday", label: "Quarta-feira" },
  { id: "thursday", label: "Quinta-feira" },
  { id: "friday", label: "Sexta-feira" },
  { id: "saturday", label: "Sábado" },
  { id: "sunday", label: "Domingo" },
];

const DEFAULT_SCHEDULE = {
  open: true,
  start: "09:00",
  end: "20:00", // Antes era 19:00
  breakStart: "12:00",
  breakEnd: "13:00",
};

const ScheduleSettings = ({ companyId }) => {
  const { companyInfo } = useAuth();
  const targetCompanyId = companyId || companyInfo?.id;

  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [bookingRules, setBookingRules] = useState({
    minNoticeHours: 2,
    maxFutureDays: 30,
    slotBufferMinutes: 10,
    onlineBookingEnabled: false,
  });

  const [slug, setSlug] = useState("");
  const [tempSlug, setTempSlug] = useState("");
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [slugError, setSlugError] = useState(null);

  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockLoading, setBlockLoading] = useState(false);
  const [newBlock, setNewBlock] = useState({
    date: "",
    startTime: "00:00",
    endTime: "23:59",
    reason: "",
  });

  const currentIdentifier = slug || targetCompanyId;
  const publicLink =
    typeof window !== "undefined"
      ? `${window.location.origin}/agendar/${currentIdentifier}`
      : "";

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!targetCompanyId) return;
      try {
        const docRef = doc(db, "companies", targetCompanyId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const settings = data.settings || {};

          if (data.slug) {
            setSlug(data.slug);
            setTempSlug(data.slug);
          }

          const loadedSchedule = {};
          DAYS_OF_WEEK.forEach((day) => {
            loadedSchedule[day.id] = settings.schedule?.[day.id] || {
              ...DEFAULT_SCHEDULE,
              open: day.id !== "sunday",
            };
          });
          setSchedule(loadedSchedule);

          setBookingRules({
            minNoticeHours: settings.bookingRules?.minNoticeHours ?? 2,
            maxFutureDays: settings.bookingRules?.maxFutureDays ?? 30,
            slotBufferMinutes: settings.bookingRules?.slotBufferMinutes ?? 10,
            onlineBookingEnabled:
              settings.bookingRules?.onlineBookingEnabled ?? false,
          });
        }
      } catch (error) {
        console.error("Erro ao carregar agenda:", error);
      }
    };

    const fetchBlocks = async () => {
      if (!targetCompanyId) return;
      try {
        const allBlocks = await getBlockedSlots(targetCompanyId);
        const storeBlocks = allBlocks.filter((b) => !b.professionalId);
        setBlockedSlots(storeBlocks);
      } catch (error) {
        console.error("Erro ao buscar bloqueios:", error);
      }
    };

    fetchSchedule();
    fetchBlocks();
  }, [targetCompanyId]);

  // --- NOVA LÓGICA DE SALVAR SLUG (VIA SERVER) ---
  const handleSaveSlug = async () => {
    const cleanSlug = tempSlug
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-");

    if (cleanSlug === slug) {
      setIsEditingSlug(false);
      return;
    }
    if (cleanSlug.length < 3) {
      setSlugError("Mínimo 3 caracteres.");
      return;
    }

    setLoading(true);
    setSlugError(null);

    try {
      // Chama a função Serverless para verificar e salvar
      const response = await fetch("/.netlify/functions/saveCompanySlug", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: targetCompanyId, slug: cleanSlug }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSlugError(data.error || "Erro ao salvar.");
      } else {
        setSlug(cleanSlug);
        setTempSlug(cleanSlug);
        setIsEditingSlug(false);
        toast.success("Link personalizado salvo!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro de conexão.");
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleChange = (dayId, field, value) => {
    setSchedule((prev) => ({
      ...prev,
      [dayId]: { ...prev[dayId], [field]: value },
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const docRef = doc(db, "companies", targetCompanyId);
      await updateDoc(docRef, {
        "settings.schedule": schedule,
        "settings.bookingRules": bookingRules,
        "settings.onlineBookingEnabled": bookingRules.onlineBookingEnabled,
      });
      toast.success("Regras salvas!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOnlineBooking = async () => {
    const newState = !bookingRules.onlineBookingEnabled;
    setBookingRules((prev) => ({ ...prev, onlineBookingEnabled: newState }));
    try {
      const docRef = doc(db, "companies", targetCompanyId);
      await updateDoc(docRef, {
        "settings.bookingRules.onlineBookingEnabled": newState,
        "settings.onlineBookingEnabled": newState,
      });
      if (newState) toast.success("Agendamento Online ATIVADO!");
      else toast.info("Agendamento Online DESATIVADO.");
    } catch (error) {
      toast.error("Erro ao salvar.");
      setBookingRules((prev) => ({ ...prev, onlineBookingEnabled: !newState }));
    }
  };

  const handleAddBlock = async (e) => {
    e.preventDefault();
    if (!newBlock.date || !newBlock.reason) {
      toast.warning("Preencha a data e o motivo.");
      return;
    }
    setBlockLoading(true);
    try {
      await addBlockedSlot(targetCompanyId, newBlock);
      toast.success("Bloqueio adicionado!");
      const allBlocks = await getBlockedSlots(targetCompanyId);
      setBlockedSlots(allBlocks.filter((b) => !b.professionalId));
      setNewBlock({
        date: "",
        startTime: "00:00",
        endTime: "23:59",
        reason: "",
      });
    } catch (error) {
      toast.error("Erro ao adicionar bloqueio.");
    } finally {
      setBlockLoading(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm("Remover este bloqueio?")) return;
    try {
      await deleteBlockedSlot(targetCompanyId, blockId);
      setBlockedSlots((prev) => prev.filter((b) => b.id !== blockId));
      toast.success("Bloqueio removido.");
    } catch (error) {
      toast.error("Erro ao remover.");
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicLink);
    toast.success("Link copiado!", {
      icon: <CheckCircle2 className="text-green-500" />,
    });
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(
      `Agende seu horário aqui: ${publicLink}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  const handleOpenLink = () => {
    window.open(publicLink, "_blank");
  };

  const inputStyles =
    "w-full pl-4 pr-2 py-2.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-md text-light-text-primary dark:text-white focus:ring-2 focus:ring-brand-blue focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-6 pb-10">
      <style>{`.dark input[type="date"]::-webkit-calendar-picker-indicator, .dark input[type="time"]::-webkit-calendar-picker-indicator { filter: invert(1) brightness(100%); cursor: pointer; }`}</style>

      {/* 1. MASTER SWITCH */}
      <div className="bg-light-card dark:bg-dark-card rounded-lg shadow border border-light-border dark:border-dark-border p-6 flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-light-text-primary dark:text-white flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-brand-blue dark:text-blue-400" />
              Agendamento Online
            </h3>
            <p className="text-sm text-light-text-secondary dark:text-gray-400 mt-1">
              Link público para clientes agendarem.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleOnlineBooking}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
              bookingRules.onlineBookingEnabled
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 ring-1 ring-green-500/50"
                : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 ring-1 ring-slate-300 dark:ring-slate-600"
            }`}
          >
            {bookingRules.onlineBookingEnabled ? (
              <>
                <ToggleRight className="w-6 h-6" /> ATIVADO
              </>
            ) : (
              <>
                <ToggleLeft className="w-6 h-6" /> DESATIVADO
              </>
            )}
          </button>
        </div>

        {/* ÁREA DE LINK E SLUG */}
        {bookingRules.onlineBookingEnabled && (
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 rounded-xl p-5 animate-in slide-in-from-top-2 duration-300">
            <h3 className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
              <LinkIcon size={16} />
              Link da sua Barbearia
            </h3>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <div className="flex-1 w-full bg-white dark:bg-dark-bg border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 shadow-sm flex items-center gap-1">
                  <span className="text-gray-400 text-sm whitespace-nowrap">
                    {window.location.host}/agendar/
                  </span>
                  {isEditingSlug ? (
                    <input
                      type="text"
                      autoFocus
                      value={tempSlug}
                      onChange={(e) => {
                        setTempSlug(
                          e.target.value.toLowerCase().replace(/\s/g, "-")
                        );
                        setSlugError(null);
                      }}
                      placeholder="nome-da-barbearia"
                      className="flex-1 bg-transparent text-sm font-bold text-brand-blue outline-none min-w-0"
                    />
                  ) : (
                    <span className="flex-1 text-sm font-bold text-gray-700 dark:text-gray-200 truncate">
                      {slug || targetCompanyId}
                    </span>
                  )}
                </div>
                {isEditingSlug ? (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveSlug}
                      disabled={loading}
                      className="px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700"
                    >
                      OK
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingSlug(false);
                        setTempSlug(slug);
                        setSlugError(null);
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-300"
                    >
                      X
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsEditingSlug(true)}
                    className="flex items-center gap-1 px-3 py-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-xs font-bold transition-colors"
                  >
                    <Edit3 size={14} /> Alterar
                  </button>
                )}
              </div>
              {slugError && (
                <p className="text-xs text-red-500 font-bold">{slugError}</p>
              )}
              {!isEditingSlug && (
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={handleCopyLink}
                    className="flex-1 flex items-center justify-center gap-2 p-2.5 bg-white dark:bg-dark-card border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 text-sm text-gray-700 dark:text-gray-200 shadow-sm"
                  >
                    <Copy size={16} /> Copiar
                  </button>
                  <button
                    onClick={handleShareWhatsApp}
                    className="flex-1 flex items-center justify-center gap-2 p-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm shadow-sm"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </button>
                  <button
                    onClick={handleOpenLink}
                    title="Abrir"
                    className="p-2.5 bg-brand-blue hover:bg-blue-700 text-white rounded-lg shadow-sm"
                  >
                    <ExternalLink size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* BLOCOS RESTANTES MANTIDOS (Horários e Bloqueios) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-light-card dark:bg-dark-card rounded-lg shadow border border-light-border dark:border-dark-border p-6">
            <h3 className="font-semibold mb-6 text-light-text-primary dark:text-white flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-brand-blue dark:text-blue-400" />{" "}
              Horário de Funcionamento
            </h3>
            <div className="space-y-4">
              {DAYS_OF_WEEK.map((day) => {
                const dayConfig = schedule[day.id] || DEFAULT_SCHEDULE;
                return (
                  <div
                    key={day.id}
                    className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border transition-all ${
                      dayConfig.open
                        ? "border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg"
                        : "bg-gray-100 dark:bg-transparent border-transparent opacity-60"
                    }`}
                  >
                    <div className="w-full sm:w-36 flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={dayConfig.open}
                        onChange={(e) =>
                          handleScheduleChange(day.id, "open", e.target.checked)
                        }
                        className="w-5 h-5 text-brand-blue rounded border-gray-300 focus:ring-brand-blue dark:bg-dark-bg dark:border-dark-border"
                      />
                      <span className="text-sm font-medium text-light-text-primary dark:text-white">
                        {day.label}
                      </span>
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-3 w-full">
                      <div className="relative">
                        <input
                          type="time"
                          disabled={!dayConfig.open}
                          value={dayConfig.start}
                          onChange={(e) =>
                            handleScheduleChange(
                              day.id,
                              "start",
                              e.target.value
                            )
                          }
                          className={inputStyles}
                        />
                      </div>
                      <div className="relative">
                        <input
                          type="time"
                          disabled={!dayConfig.open}
                          value={dayConfig.end}
                          onChange={(e) =>
                            handleScheduleChange(day.id, "end", e.target.value)
                          }
                          className={inputStyles}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8">
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 px-4 py-3 text-sm font-medium text-white rounded-lg transition-all duration-200 bg-brand-blue hover:bg-brand-blue/90 dark:bg-blue-600 dark:hover:bg-blue-500 shadow-md disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <>
                    <Save className="w-5 h-5" /> Salvar Regras
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-light-card dark:bg-dark-card rounded-lg shadow border border-light-border dark:border-dark-border p-6 h-full flex flex-col">
            <h3 className="font-semibold mb-6 text-light-text-primary dark:text-white flex items-center gap-2 text-lg">
              <Ban className="w-5 h-5 text-red-500" /> Bloqueios e Feriados
            </h3>
            <form
              onSubmit={handleAddBlock}
              className="bg-gray-50 dark:bg-dark-bg p-5 rounded-lg border border-light-border dark:border-dark-border mb-6"
            >
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-light-text-secondary dark:text-gray-400 uppercase mb-1.5 block">
                    Motivo
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Feriado..."
                    className={inputStyles}
                    value={newBlock.reason}
                    onChange={(e) =>
                      setNewBlock({ ...newBlock, reason: e.target.value })
                    }
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="text-xs font-bold text-light-text-secondary dark:text-gray-400 uppercase mb-1.5 block">
                      Data
                    </label>
                    <input
                      type="date"
                      required
                      className={inputStyles}
                      value={newBlock.date}
                      onChange={(e) =>
                        setNewBlock({ ...newBlock, date: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-light-text-secondary dark:text-gray-400 uppercase mb-1.5 block">
                      Início
                    </label>
                    <input
                      type="time"
                      required
                      className={inputStyles}
                      value={newBlock.startTime}
                      onChange={(e) =>
                        setNewBlock({ ...newBlock, startTime: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-light-text-secondary dark:text-gray-400 uppercase mb-1.5 block">
                      Fim
                    </label>
                    <input
                      type="time"
                      required
                      className={inputStyles}
                      value={newBlock.endTime}
                      onChange={(e) =>
                        setNewBlock({ ...newBlock, endTime: e.target.value })
                      }
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={blockLoading}
                  className="w-full py-2.5 bg-transparent border-2 border-dashed border-light-border dark:border-dark-border text-light-text-secondary dark:text-gray-400 hover:border-brand-blue rounded-lg font-bold text-sm flex items-center justify-center gap-2 mt-4"
                >
                  {blockLoading ? (
                    "Adicionando..."
                  ) : (
                    <>
                      <Plus className="w-4 h-4" /> Adicionar Bloqueio
                    </>
                  )}
                </button>
              </div>
            </form>
            <div className="flex-1 overflow-y-auto max-h-[400px] space-y-2 pr-1 custom-scrollbar">
              {blockedSlots.length === 0 ? (
                <div className="h-32 flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-light-border rounded-lg">
                  <CalendarCheck className="w-8 h-8 mb-2 opacity-20" />
                  <p className="text-sm">Nenhum bloqueio.</p>
                </div>
              ) : (
                blockedSlots.map((block) => (
                  <div
                    key={block.id}
                    className="flex items-center justify-between p-4 bg-light-bg dark:bg-dark-bg border border-light-border rounded-lg hover:border-red-200 group shadow-sm"
                  >
                    <div>
                      <p className="font-bold text-light-text-primary dark:text-white text-sm flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block"></span>
                        {block.reason}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-light-text-secondary dark:text-gray-400 mt-1 pl-3.5">
                        <span className="font-mono bg-white dark:bg-dark-card border px-1.5 py-0.5 rounded">
                          {new Date(
                            block.date + "T00:00:00"
                          ).toLocaleDateString("pt-BR")}
                        </span>
                        <span>|</span>
                        <span>
                          {block.startTime} às {block.endTime}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteBlock(block.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title="Remover"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSettings;
